import { Button, Select } from '@react-apps/design-system';
import type { UseQueryResult } from '@tanstack/react-query';
import { scopeOptionLabel } from '@/shared/scopes/use-scopes';
import type { Scope } from '@/shared/scopes/scopes-api.datasource';

interface ScopeSelectProps {
  label: string;
  value: string;
  onChange: (scopeId: string) => void;
  source: UseQueryResult<Scope[]>;
  emptyPlaceholder: string;
}

/**
 * O seletor de plataforma dos dois modais de acesso. A fonte vem de fora — a
 * lista inteira para o admin, a que o iamEditor administra para ele —, e o
 * erro de carga fica aqui, com o "Tentar de novo", para o modal não oferecer
 * uma lista vazia como se fosse a resposta.
 */
export function ScopeSelect({
  label,
  value,
  onChange,
  source,
  emptyPlaceholder,
}: ScopeSelectProps) {
  return (
    <div className="space-y-2">
      <Select
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={
          source.isLoading ? 'Carregando plataformas…' : emptyPlaceholder
        }
        error={
          source.isError
            ? 'Não foi possível carregar as plataformas'
            : undefined
        }
        options={(source.data ?? []).map((s) => ({
          value: s.id,
          label: scopeOptionLabel(s),
        }))}
      />
      {source.isError && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => void source.refetch()}
        >
          Tentar de novo
        </Button>
      )}
    </div>
  );
}

/**
 * O botão que abre o modal no modo iamEditor, na toolbar da lista e no
 * detalhe. Carregando, não renderiza (a toolbar não pula); sem plataforma
 * nenhuma, uma linha de texto no lugar; com erro, o botão aparece, e o modal
 * mostra o erro com o "Tentar de novo".
 */
export function ScopedAccessButton({
  source,
  onClick,
  children,
  compact = false,
}: {
  source: UseQueryResult<Scope[]>;
  onClick: () => void;
  children: React.ReactNode;
  /** O do detalhe: secundário e pequeno, como o "Criar perfil" do admin ao lado. */
  compact?: boolean;
}) {
  if (source.isLoading) return null;
  if (source.isSuccess && source.data.length === 0) {
    return (
      <p className="text-(--color-text-secondary) text-sm">
        Você ainda não administra nenhuma plataforma — peça a um admin.
      </p>
    );
  }
  return (
    <Button
      variant={compact ? 'secondary' : undefined}
      size={compact ? 'sm' : undefined}
      onClick={onClick}
    >
      {children}
    </Button>
  );
}
